import { useState } from 'react';

import { IconCopy, IconRefresh } from '@tabler/icons-react';
import { motion } from 'motion/react';
import { toast } from 'react-hot-toast';

import { cn } from '@/utils/tailwind';

import { useRequiredSmartSearchChatContext } from '../../../SmartSearchChatContext';
import { PathwayUIMessage } from '../../../types';
import ActionIcon from '../../ActionIcon';
import Time from '../../Time';

import { Step } from './components';
import { getCreatedAt, getPlainText, groupIntoSteps } from './utils';

interface AssistantMessageProps {
  message: PathwayUIMessage;
}

const AssistantMessage: React.FC<AssistantMessageProps> = ({ message }) => {
  const [hover, setHover] = useState(false);
  const chat = useRequiredSmartSearchChatContext();
  const isStreaming =
    chat.status === 'streaming' || chat.status === 'submitted';

  const handleCopy = () => {
    navigator.clipboard.writeText(getPlainText(message));
    toast.success('Copied to clipboard');
  };

  const handleRegenerate = () => {
    if (isStreaming) return;
    chat.regenerate({ messageId: message.id });
  };

  const steps = groupIntoSteps(message.parts);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.4 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="flex flex-col gap-1">
        {steps.map((stepParts, i) => (
          <Step
            key={i}
            stepParts={stepParts}
            shouldCollapse={i !== steps.length - 1}
          />
        ))}
      </div>
      <div
        className={cn('flex items-center pt-1 opacity-0 transition-opacity', {
          'opacity-100': hover,
        })}
      >
        <Time timestamp={getCreatedAt(message)} className="mr-3" />
        <ActionIcon icon={IconCopy} tooltip="Copy" onClick={handleCopy} />
        <ActionIcon
          icon={IconRefresh}
          tooltip="Regenerate"
          onClick={handleRegenerate}
          disabled={isStreaming}
        />
      </div>
    </motion.div>
  );
};

export default AssistantMessage;
